import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ShieldCheck, Users, FileText, ArrowDown, Menu, X, Linkedin, Facebook, Youtube, Instagram, Twitter, MapPin, Upload, CheckCircle, Download, Settings } from 'lucide-react';

const Landing = () => {
  const navigate = useNavigate();
  const [showOptions, setShowOptions] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sectionsVisible, setSectionsVisible] = useState({});
  const sectionRefs = useRef({});

  useEffect(() => {
    setIsVisible(true);

    // Intersection Observer for scroll animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setSectionsVisible((prev) => ({
              ...prev,
              [entry.target.dataset.section]: true,
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const handlePersonalSignup = () => {
    // Navigate directly to login page with supervisor user type
    navigate('/login', {
      state: {
        userType: 'patient',
        fromLanding: true
      }
    });
  };

  const handleClinicSignup = () => {
    // Navigate directly to login page with clinic user type
    navigate('/login', {
      state: {
        userType: 'clinic',
        fromLanding: true
      }
    });
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const handleRegister = (userType = 'clinic') => {
    // Navigate to register page with state to pre-select user type
    navigate('/register', {
      state: {
        userType: userType,
        fromLanding: true
      }
    });
  };

  return (
    <div className="w-full min-h-screen bg-white m-0 p-0">
      {/* Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 py-4 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-center">
          {/* Center Navigation Pill with Logo, Links and Buttons ALL INSIDE */}
          <div className="hidden lg:flex items-center bg-white/95 backdrop-blur-lg rounded-full shadow-lg px-6 py-2.5 gap-6 border border-gray-100">
            {/* Logo INSIDE Pill */}
            <div className="flex items-center pr-4 border-r border-gray-200">
              <img src="/aims-logo.webp" alt="AIMS Logo" className="h-10" />
            </div>

            {/* Navigation Links */}
            <a href="#features" className="text-gray-700 hover:text-gray-900 text-base font-semibold transition-colors whitespace-nowrap">
              Features
            </a>
            <a href="#how-it-works" className="text-gray-700 hover:text-gray-900 text-base font-semibold transition-colors whitespace-nowrap">
              How it works
            </a>
            <a href="#pricing" className="text-gray-700 hover:text-gray-900 text-base font-semibold transition-colors whitespace-nowrap">
              Pricing
            </a>

            {/* Action Buttons Inside Same Pill */}
            <button
              onClick={handlePersonalSignup}
              className="bg-[#1e3a5f] hover:bg-[#152d4a] text-white px-5 py-2 rounded-full text-[13px] font-medium transition-all whitespace-nowrap shadow-sm"
            >
              Start Free Trial
            </button>
            <button
              onClick={handleClinicSignup}
              className="bg-gray-900 hover:bg-black text-white px-5 py-2 rounded-full text-[13px] font-medium transition-all whitespace-nowrap shadow-sm"
            >
              Request Demo
            </button>
          </div>

          {/* Mobile - Logo and Menu Button */}
          <div className="lg:hidden flex items-center justify-between w-full">
            <div className="flex items-center">
              <img src="/aims-logo.webp" alt="AIMS Logo" className="h-10" />
            </div>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-gray-800 transition-transform duration-300"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Sidebar Menu Overlay */}
      {mobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />

          {/* Sidebar */}
          <div className="fixed top-0 left-0 h-full w-80 bg-white z-50 lg:hidden shadow-2xl transform transition-transform duration-300">
            <div className="p-6">
              {/* Logo */}
              <div className="flex items-center mb-12 mt-2">
                <img src="/aims-logo.webp" alt="AIMS Logo" className="h-12" />
              </div>

              {/* Navigation Links */}
              <div className="flex flex-col space-y-6 mb-12">
                <a href="#features" className="text-gray-900 hover:text-gray-600 text-lg font-normal transition-colors">
                  Features
                </a>
                <a href="#how-it-works" className="text-gray-900 hover:text-gray-600 text-lg font-normal transition-colors">
                  How it works
                </a>
                <a href="#pricing" className="text-gray-900 hover:text-gray-600 text-lg font-normal transition-colors">
                  Pricing
                </a>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col space-y-3">
                <button
                  onClick={handlePersonalSignup}
                  className="bg-[#1e3a5f] hover:bg-[#152d4a] text-white px-6 py-3 rounded-full text-base font-medium transition-all w-full"
                >
                  Start Free Trial
                </button>
                <button
                  onClick={handleClinicSignup}
                  className="bg-gray-900 hover:bg-black text-white px-6 py-3 rounded-full text-base font-medium transition-all w-full"
                >
                  Request Demo
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Hero Section - Myndlift Style */}
      <section
        id="start"
        className="relative w-full bg-white pt-2 sm:pt-3 pb-4 sm:pb-6 px-1 sm:px-2 md:px-3 lg:px-4"
        style={{ opacity: 1, transform: 'none' }}
      >
        {/* Hero Container with Rounded Corners */}
        <div className="w-[99.5%] mx-auto relative overflow-hidden rounded-[1.5rem] sm:rounded-[2rem] md:rounded-[2.5rem] lg:rounded-[3rem]"
          style={{
            minHeight: 'clamp(400px, 60vh, 650px)'
          }}>

          {/* Video Background */}
          <div className="absolute inset-0">
            <video
              src="/hero_section.mp4"
              loop
              preload="auto"
              muted
              playsInline
              autoPlay
              style={{
                cursor: 'auto',
                width: '100%',
                height: '100%',
                borderRadius: '0px',
                display: 'block',
                objectFit: 'cover',
                backgroundColor: 'rgb(133, 119, 95)',
                objectPosition: '50% 50%'
              }}
            />
          </div>

          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30" />

          {/* Content Section */}
          <section
            className="relative z-10 h-full flex flex-col justify-center px-6 sm:px-10 md:px-14 lg:px-20 py-16 sm:py-20 md:py-24"
            style={{ opacity: 1, transform: 'none' }}
          >
            <div className="max-w-2xl lg:max-w-3xl">
              {/* Text Container */}
              <div className="mb-8">
                {/* Main Heading */}
                <div className="framer-37bogi" style={{ justifyContent: 'center', transform: 'none' }}>
                  <h1
                    className="framer-text framer-styles-preset-1r94ks7"
                    style={{
                      '--framer-text-alignment': 'left',
                      '--framer-text-color': 'rgb(255, 255, 255)',
                      fontSize: 'clamp(2.5rem, 6vw, 5rem)',
                      fontWeight: '700',
                      lineHeight: '1.1',
                      letterSpacing: '-0.02em',
                      marginBottom: '1.5rem',
                      color: '#ffffff',
                      textShadow: '0 2px 8px rgba(0,0,0,0.5)'
                    }}
                  >
                    AIMS - Asset Information Management System
                  </h1>
                </div>

                {/* Subtitle */}
                <div className="framer-fun1vz" style={{ justifyContent: 'center', transform: 'none' }}>
                  <h5
                    className="framer-text framer-styles-preset-1v4ddqz"
                    style={{
                      '--framer-text-alignment': 'left',
                      '--framer-text-color': 'rgb(255, 255, 255)',
                      fontSize: 'clamp(1.5rem, 3vw, 2.5rem)',
                      fontWeight: '600',
                      lineHeight: '1.4',
                      letterSpacing: '0.05em',
                      maxWidth: '42rem',
                      color: '#ffffff',
                      textShadow: '0 2px 4px rgba(0,0,0,0.5)'
                    }}
                  >
                    Industrial P&ID Processing and Tag Management Platform
                  </h5>
                </div>

                {/* Description Text */}
                <div className="mt-6">
                  <p
                    className="framer-text"
                    style={{
                      fontSize: 'clamp(1rem, 1.5vw, 1.125rem)',
                      fontWeight: '400',
                      lineHeight: '1.6',
                      maxWidth: '42rem',
                      color: '#ffffff',
                      textShadow: '0 2px 4px rgba(0,0,0,0.5)'
                    }}
                  >
                    Transform your P&ID workflows with AI-powered tag extraction, ISA 5.1 compliance, and automated ADNOC tag sheet generation. Achieve 90% faster processing with zero manual errors.
                  </p>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  onClick={handlePersonalSignup}
                  className="inline-block cursor-pointer"
                  style={{ transform: 'none' }}
                >
                  <div style={{ transform: 'none' }}>
                    <p
                      className="framer-text bg-[#1e3a5f] hover:bg-[#152d4a] text-white px-8 py-4 rounded-full text-base font-medium transition-all duration-300 transform hover:scale-105 shadow-lg"
                      style={{
                        textAlign: 'center',
                        color: 'rgb(255, 255, 255)'
                      }}
                    >
                      Start Free Trial
                    </p>
                  </div>
                </a>

                <a
                  onClick={handleClinicSignup}
                  className="inline-block cursor-pointer"
                  style={{ transform: 'none' }}
                >
                  <div style={{ transform: 'none' }}>
                    <p
                      className="framer-text bg-gray-900 hover:bg-black text-white px-8 py-4 rounded-full text-base font-medium transition-all duration-300 transform hover:scale-105 shadow-lg"
                      style={{
                        textAlign: 'center',
                        color: 'rgb(255, 255, 255)'
                      }}
                    >
                      Request Demo
                    </p>
                  </div>
                </a>
              </div>
            </div>
          </section>

          {/* Scroll Indicator - Arrow Down */}
          <div
            className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 hidden sm:block"
            style={{ borderRadius: 'inherit' }}
          >
            <div className="flex flex-col items-center">
              <ArrowDown
                className="h-6 w-6 text-white/70"
                style={{
                  animation: 'bounce-smooth 2s ease-in-out infinite'
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Options Modal - Fixed Position Overlay */}
      {showOptions && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/60 z-[100] backdrop-blur-sm"
            onClick={() => setShowOptions(false)}
            style={{
              animation: 'fadeIn 0.3s ease-out'
            }}
          />

          {/* Modal Content */}
          <div
            className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[101] w-[90%] sm:w-auto"
            style={{
              animation: 'slideUp 0.4s ease-out'
            }}
          >
            <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-md mx-auto shadow-2xl">
              <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-3 text-center">
                {selectedType === 'personal' ? "Personal Account" : "Clinic Account"}
              </h3>
              <p className="text-gray-600 mb-6 text-sm sm:text-base text-center">
                {selectedType === 'personal'
                  ? "Get started with industrial asset management"
                  : "Register your facility for professional P&ID management"
                }
              </p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => handleRegister(selectedType === 'personal' ? 'patient' : 'clinic')}
                  className="bg-[#323956] hover:bg-[#2a3048] text-white px-6 py-3.5 rounded-full font-medium transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  Create New Account
                </button>
                <button
                  onClick={handleLogin}
                  className="bg-gray-900 hover:bg-black text-white px-6 py-3.5 rounded-full font-medium transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  I Already Have an Account
                </button>
                <button
                  onClick={() => setShowOptions(false)}
                  className="text-sm text-gray-600 hover:text-gray-900 underline mt-2 font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      <style jsx>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes bounce-smooth {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(0, 137, 123, 0.4);
          }
          50% {
            transform: scale(1.02);
            box-shadow: 0 0 20px 10px rgba(0, 137, 123, 0);
          }
        }

        @keyframes neuronPulse {
          0%, 100% {
            opacity: 0.6;
            transform: scale(0.98);
          }
          50% {
            opacity: 1;
            transform: scale(1.02);
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes glow {
          0%, 100% {
            box-shadow: 0 0 10px rgba(0, 137, 123, 0.2);
          }
          50% {
            box-shadow: 0 0 25px rgba(0, 137, 123, 0.6);
          }
        }

        .animate-pulse-card {
          animation: pulse 2s ease-in-out infinite;
        }

        .animate-neuron {
          animation: neuronPulse 2.5s ease-in-out infinite;
        }

        .animate-fade-in-up {
          animation: fadeInUp 0.8s ease-out forwards;
        }

        .animate-glow {
          animation: glow 2s ease-in-out infinite;
        }

        .delay-100 { animation-delay: 0.1s; }
        .delay-200 { animation-delay: 0.2s; }
        .delay-300 { animation-delay: 0.3s; }
        .delay-400 { animation-delay: 0.4s; }
        .delay-500 { animation-delay: 0.5s; }
        .delay-600 { animation-delay: 0.6s; }

        @keyframes dashFlow {
          0% {
            background-position: 0 0;
          }
          100% {
            background-position: 0 40px;
          }
        }

        @keyframes dotPulse {
          0%, 100% {
            transform: translate(-50%, -50%) scale(1);
            box-shadow: 0 0 0 0 rgba(0, 137, 123, 0.7);
          }
          50% {
            transform: translate(-50%, -50%) scale(1.1);
            box-shadow: 0 0 0 10px rgba(0, 137, 123, 0);
          }
        }

        @keyframes lineGrow {
          from {
            height: 0;
            opacity: 0;
          }
          to {
            height: 100%;
            opacity: 1;
          }
        }
      `}</style>

      {/* Features Section */}
      <section
        id="features"
        ref={(el) => (sectionRefs.current.features = el)}
        data-section="features"
        className={`py-12 sm:py-16 md:py-20 bg-white transition-all duration-1000 relative ${
          sectionsVisible.features ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Section Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Powerful Features for Industrial Asset Management
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Everything you need to process P&ID documents, extract tags, and generate compliant tag sheets
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {/* Feature 1 */}
            <div className="bg-white rounded-3xl p-8 shadow-lg border border-gray-200 hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                <Upload className="w-8 h-8 text-[#1e3a5f]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">P&ID Document Processing</h3>
              <p className="text-gray-600 leading-relaxed">
                Upload and parse technical drawings in PDF format. Our AI engine automatically identifies and extracts instrumentation symbols and tags.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-white rounded-3xl p-8 shadow-lg border border-gray-200 hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-green-100 rounded-xl flex items-center justify-center mb-6">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">ISA 5.1 Compliant Tag Extraction</h3>
              <p className="text-gray-600 leading-relaxed">
                Automatically extract tags following ISA 5.1 instrumentation standards. Ensure complete compliance with industry requirements.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-white rounded-3xl p-8 shadow-lg border border-gray-200 hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-purple-100 rounded-xl flex items-center justify-center mb-6">
                <FileText className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">ADNOC Tag Creation Sheet Generation</h3>
              <p className="text-gray-600 leading-relaxed">
                Generate standardized ADNOC tag creation sheets automatically. Export to Excel/CSV formats with one click.
              </p>
            </div>

            {/* Feature 4 */}
            <div className="bg-white rounded-3xl p-8 shadow-lg border border-gray-200 hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-orange-100 rounded-xl flex items-center justify-center mb-6">
                <Settings className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Multi-Project Area Management</h3>
              <p className="text-gray-600 leading-relaxed">
                Organize and manage multiple project areas simultaneously. Track progress across different facilities and zones.
              </p>
            </div>

            {/* Feature 5 */}
            <div className="bg-white rounded-3xl p-8 shadow-lg border border-gray-200 hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-teal-100 rounded-xl flex items-center justify-center mb-6">
                <Users className="w-8 h-8 text-teal-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Real-time Collaboration</h3>
              <p className="text-gray-600 leading-relaxed">
                Engineers and supervisors work together seamlessly. Review, approve, and annotate tag sheets in real-time.
              </p>
            </div>

            {/* Feature 6 */}
            <div className="bg-white rounded-3xl p-8 shadow-lg border border-gray-200 hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-red-100 rounded-xl flex items-center justify-center mb-6">
                <ShieldCheck className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Compliance and Audit Trail</h3>
              <p className="text-gray-600 leading-relaxed">
                Complete audit trail for all changes. Maintain compliance with IEC 61511, API 14C, and ASME B31.3 standards.
              </p>
            </div>
          </div>

          {/* How It Works Section */}
          <div id="how-it-works" className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 sm:p-10 md:p-12 mb-12 shadow-lg border border-gray-200 animate-fade-in-up delay-300 relative z-10">
            <div className="text-center mb-10">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                How It Works
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Five simple steps to transform your P&ID processing workflow
              </p>
            </div>

            <div className="grid md:grid-cols-5 gap-6">
              {/* Step 1 */}
              <div className="text-center">
                <div className="w-16 h-16 bg-[#1e3a5f] rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">
                  1
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Upload P&ID</h3>
                <p className="text-sm text-gray-600">Upload your technical drawings in PDF format</p>
              </div>

              {/* Step 2 */}
              <div className="text-center">
                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">
                  2
                </div>
                <h3 className="font-bold text-gray-900 mb-2">AI Extraction</h3>
                <p className="text-sm text-gray-600">Automatic tag extraction using AI intelligence</p>
              </div>

              {/* Step 3 */}
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">
                  3
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Generate Sheets</h3>
                <p className="text-sm text-gray-600">Create standardized ADNOC tag sheets</p>
              </div>

              {/* Step 4 */}
              <div className="text-center">
                <div className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">
                  4
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Review & Approve</h3>
                <p className="text-sm text-gray-600">Team collaboration for verification</p>
              </div>

              {/* Step 5 */}
              <div className="text-center">
                <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">
                  5
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Export Data</h3>
                <p className="text-sm text-gray-600">Download Excel/CSV formatted files</p>
              </div>
            </div>
          </div>

          {/* Industry Standards Box */}
          <div className="bg-gradient-to-br from-blue-50 to-white rounded-3xl p-8 sm:p-10 md:p-12 mb-12 shadow-lg border border-blue-200 animate-fade-in-up delay-500 relative z-10">
            <div className="text-center mb-8">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Industry Standards Compliance
              </h2>
              <p className="text-lg text-gray-600">
                AIMS ensures full compliance with all major industrial instrumentation and safety standards
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-xl border-2 border-blue-200 text-center">
                <div className="text-3xl font-bold text-[#1e3a5f] mb-2">ISA 5.1</div>
                <p className="text-sm text-gray-600">Instrumentation Symbols and Identification</p>
              </div>
              <div className="bg-white p-6 rounded-xl border-2 border-green-200 text-center">
                <div className="text-3xl font-bold text-green-600 mb-2">ASME B31.3</div>
                <p className="text-sm text-gray-600">Process Piping Standards</p>
              </div>
              <div className="bg-white p-6 rounded-xl border-2 border-orange-200 text-center">
                <div className="text-3xl font-bold text-orange-600 mb-2">API 14C</div>
                <p className="text-sm text-gray-600">Safety Systems Requirements</p>
              </div>
              <div className="bg-white p-6 rounded-xl border-2 border-purple-200 text-center">
                <div className="text-3xl font-bold text-purple-600 mb-2">IEC 61511</div>
                <p className="text-sm text-gray-600">Functional Safety Standards</p>
              </div>
            </div>
          </div>

          {/* Benefits Cards with 3D Animation */}
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Key Benefits
            </h2>
            <p className="text-lg text-gray-600">
              Transform your engineering workflows and achieve unprecedented efficiency
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-4" style={{ perspective: '1500px' }}>
            {/* Benefit 1: Speed */}
            <div className="group" style={{ transformStyle: 'preserve-3d' }}>
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/60 to-blue-700/60 rounded-2xl blur-2xl group-hover:blur-3xl opacity-50 transition-all duration-700"
                     style={{ transform: 'translateZ(-30px)' }}></div>
                <div className="bg-gradient-to-br from-[#1e3a5f] to-[#152d4a] rounded-2xl p-6 sm:p-8 text-white text-center cursor-pointer transition-all duration-700 group-hover:shadow-[0_25px_50px_-12px_rgba(30,58,95,0.6)]"
                     style={{
                       transformStyle: 'preserve-3d',
                       animation: 'float 3s ease-in-out infinite',
                       transform: 'translateZ(20px)'
                     }}
                     onMouseEnter={(e) => e.currentTarget.style.transform = 'translateZ(20px) scale(1.05) rotateY(5deg)'}
                     onMouseLeave={(e) => e.currentTarget.style.transform = 'translateZ(20px) scale(1) rotateY(0deg)'}>
                  <div className="text-5xl font-bold mb-3">90%</div>
                  <h3 className="text-xl sm:text-2xl font-semibold mb-3">Faster Tag Extraction</h3>
                  <p className="text-sm sm:text-base opacity-90">Complete projects in days, not weeks</p>
                </div>
              </div>
            </div>

            {/* Benefit 2: Accuracy */}
            <div className="group" style={{ transformStyle: 'preserve-3d' }}>
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-green-500/60 to-green-700/60 rounded-2xl blur-2xl group-hover:blur-3xl opacity-50 transition-all duration-700"
                     style={{ transform: 'translateZ(-30px)' }}></div>
                <div className="bg-gradient-to-br from-green-600 to-green-800 rounded-2xl p-6 sm:p-8 text-white text-center cursor-pointer transition-all duration-700 group-hover:shadow-[0_25px_50px_-12px_rgba(34,197,94,0.6)]"
                     style={{
                       transformStyle: 'preserve-3d',
                       animation: 'float 3s ease-in-out infinite 0.5s',
                       transform: 'translateZ(20px)'
                     }}
                     onMouseEnter={(e) => e.currentTarget.style.transform = 'translateZ(20px) scale(1.05) rotateY(5deg)'}
                     onMouseLeave={(e) => e.currentTarget.style.transform = 'translateZ(20px) scale(1) rotateY(0deg)'}>
                  <div className="text-5xl font-bold mb-3">0</div>
                  <h3 className="text-xl sm:text-2xl font-semibold mb-3">Manual Errors</h3>
                  <p className="text-sm sm:text-base opacity-90">AI-powered accuracy guarantee</p>
                </div>
              </div>
            </div>

            {/* Benefit 3: Compliance */}
            <div className="group" style={{ transformStyle: 'preserve-3d' }}>
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-orange-500/60 to-orange-700/60 rounded-2xl blur-2xl group-hover:blur-3xl opacity-50 transition-all duration-700"
                     style={{ transform: 'translateZ(-30px)' }}></div>
                <div className="bg-gradient-to-br from-orange-600 to-orange-800 rounded-2xl p-6 sm:p-8 text-white text-center cursor-pointer transition-all duration-700 group-hover:shadow-[0_25px_50px_-12px_rgba(249,115,22,0.6)]"
                     style={{
                       transformStyle: 'preserve-3d',
                       animation: 'float 3s ease-in-out infinite 1s',
                       transform: 'translateZ(20px)'
                     }}
                     onMouseEnter={(e) => e.currentTarget.style.transform = 'translateZ(20px) scale(1.05) rotateY(5deg)'}
                     onMouseLeave={(e) => e.currentTarget.style.transform = 'translateZ(20px) scale(1) rotateY(0deg)'}>
                  <div className="text-5xl font-bold mb-3">100%</div>
                  <h3 className="text-xl sm:text-2xl font-semibold mb-3">Industry Compliance</h3>
                  <p className="text-sm sm:text-base opacity-90">Full audit trail and standards adherence</p>
                </div>
              </div>
            </div>
          </div>

          {/* Pricing Section */}
          <div id="pricing" className="text-center mb-16 animate-fade-in-up delay-600 relative z-10">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 bg-white">
              Flexible Pricing Plans
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-xl mx-auto mb-12 bg-white">
              Choose the plan that fits your facility size and project requirements
            </p>

            {/* Pricing Cards Coming Soon */}
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-3xl p-8 shadow-lg border border-gray-200">
                <p className="text-lg text-gray-700 text-center">
                  Pricing plans tailored to your facility size and project requirements. Contact us for custom quotes and enterprise solutions.
                </p>
                <div className="mt-6 flex justify-center gap-4">
                  <button onClick={handlePersonalSignup} className="bg-[#1e3a5f] hover:bg-[#152d4a] text-white px-6 py-3 rounded-full font-medium transition-all">
                    Start Free Trial
                  </button>
                  <button onClick={handleClinicSignup} className="bg-gray-900 hover:bg-black text-white px-6 py-3 rounded-full font-medium transition-all">
                    Request Demo
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-12 sm:py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-8">
            {/* Left Column - Brand & Newsletter */}
            <div className="lg:col-span-1 space-y-6">
              {/* Logo */}
              <div className="flex items-center">
                <img src="/aims-logo.webp" alt="AIMS Logo" className="h-12 brightness-0 invert" />
              </div>

              {/* Tagline */}
              <p className="text-base">
                Industrial Asset Management - <span className="text-[#1e3a5f]">Transforming P&ID workflows</span>
              </p>

              {/* Newsletter */}
              <div className="space-y-3">
                <p className="text-sm text-gray-400">
                  Get the latest updates on industrial automation and asset management delivered to your inbox.
                </p>
                <div className="space-y-2">
                  <input
                    type="email"
                    placeholder="Your email address"
                    className="w-full px-4 py-2.5 bg-gray-800 text-white rounded-lg text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00897B]"
                  />
                  <button className="w-full px-6 py-2.5 bg-[#00897B] hover:bg-[#00796B] text-white rounded-lg text-sm font-medium transition-colors">
                    Subscribe
                  </button>
                </div>
              </div>

              {/* Social Icons */}
              <div className="flex gap-3">
                <a href="#" className="w-8 h-8 bg-gray-800 hover:bg-gray-700 rounded-full flex items-center justify-center transition-colors">
                  <Linkedin className="h-4 w-4" />
                </a>
                <a href="#" className="w-8 h-8 bg-gray-800 hover:bg-gray-700 rounded-full flex items-center justify-center transition-colors">
                  <Facebook className="h-4 w-4" />
                </a>
                <a href="#" className="w-8 h-8 bg-gray-800 hover:bg-gray-700 rounded-full flex items-center justify-center transition-colors">
                  <Youtube className="h-4 w-4" />
                </a>
                <a href="#" className="w-8 h-8 bg-gray-800 hover:bg-gray-700 rounded-full flex items-center justify-center transition-colors">
                  <Instagram className="h-4 w-4" />
                </a>
                <a href="#" className="w-8 h-8 bg-gray-800 hover:bg-gray-700 rounded-full flex items-center justify-center transition-colors">
                  <Twitter className="h-4 w-4" />
                </a>
              </div>

              {/* Get Started CTA */}
              <div className="p-4 bg-[#00897B]/20 rounded-lg border border-[#00897B]/30">
                <p className="text-sm font-medium mb-1">Ready to start?</p>
                <a href="/register" className="text-sm text-[#00897B] hover:underline">Start your free trial →</a>
              </div>

              {/* Address */}
              <div className="flex items-start gap-2 text-xs text-gray-400">
                <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-white">AIMS Inc.</p>
                  <p>175 Varick Street</p>
                  <p>New York, NY 10014, US</p>
                </div>
              </div>
            </div>

            {/* For Facilities Column */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">For Facilities</h3>
              <ul className="space-y-3">
                <li><a href="#" className="text-base hover:text-[#00897B] transition-colors">Request Demo</a></li>
                <li><a href="#" className="text-base hover:text-[#00897B] transition-colors">Enterprise Solutions</a></li>
                <li><a href="#" className="text-base hover:text-[#00897B] transition-colors">Success Stories</a></li>
                <li><a href="/login" className="text-base hover:text-[#00897B] transition-colors">AIMS Dashboard →</a></li>
              </ul>
            </div>

            {/* Resources Column */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Resources</h3>
              <ul className="space-y-3">
                <li><a href="#how-it-works" className="text-base hover:text-[#00897B] transition-colors">How It Works</a></li>
                <li><a href="#" className="text-base hover:text-[#00897B] transition-colors">Guide to P&ID Standards</a></li>
                <li><a href="#" className="text-base hover:text-[#00897B] transition-colors">ISA 5.1 Documentation</a></li>
                <li><a href="#" className="text-base hover:text-[#00897B] transition-colors">Case Studies</a></li>
                <li><a href="#" className="text-base hover:text-[#00897B] transition-colors">Blog</a></li>
                <li><Link to="/faq" className="text-base hover:text-[#00897B] transition-colors">FAQs</Link></li>
                <li><a href="#" className="text-base hover:text-[#00897B] transition-colors">API Documentation</a></li>
              </ul>
            </div>

            {/* For Engineers Column */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">For Engineers</h3>
              <ul className="space-y-3">
                <li><a href="/register" className="text-base hover:text-[#00897B] transition-colors">Start Free Trial</a></li>
                <li><a href="#features" className="text-base hover:text-[#00897B] transition-colors">Key Features</a></li>
                <li><a href="#" className="text-base hover:text-[#00897B] transition-colors">AIMS vs. Manual Process</a></li>
                <li><a href="#" className="text-base hover:text-[#00897B] transition-colors">Integration Guide</a></li>
              </ul>
            </div>

            {/* Company Column */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Company</h3>
              <ul className="space-y-3">
                <li><Link to="/about-us" className="text-base hover:text-[#00897B] transition-colors">About Us</Link></li>
                <li><a href="#" className="text-base hover:text-[#00897B] transition-colors">Careers →</a></li>
                <li><Link to="/privacy-policy" className="text-base hover:text-[#00897B] transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms-and-conditions" className="text-base hover:text-[#00897B] transition-colors">Terms & Conditions</Link></li>
                <li><a href="tel:+18553217800" className="text-base hover:text-[#00897B] transition-colors">+1 (855) 321-7800</a></li>
              </ul>
            </div>
          </div>

          {/* Bottom Copyright */}
          <div className="mt-12 pt-8 border-t border-gray-800">
            <div className="text-center space-y-2">
              <p className="text-sm text-gray-400">&copy; 2024 AIMS Inc. All rights reserved.</p>
              <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                <span>Developed by</span>
                <a href="https://bettroi.com" target="_blank" rel="noopener noreferrer" className="text-[#00897B] hover:text-[#00796B] font-medium transition-colors">
                  Bettroi.com
                </a>
                <span>|</span>
                <span>Powered by</span>
                <a href="https://drmhope.com" target="_blank" rel="noopener noreferrer" className="text-[#00897B] hover:text-[#00796B] font-medium transition-colors">
                  DrMhope.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
